import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Shield, Check, Loader2, ScrollText, 
  AlertTriangle, Car, ArrowLeft, Pen, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { usePaymentMode } from '../contexts/PaymentModeContext';

const HIREHUB_ABN = "12 345 678 901";
const PLATFORM_NAME = "HireHub Australia";

const HostAgreementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user, refreshUser } = useAuth();
  const { isHostDirect } = usePaymentMode();
  
  // Check if this is view-only mode (from Agreements page)
  const isViewMode = searchParams.get('view') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [typedSignature, setTypedSignature] = useState('');
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [termsRead, setTermsRead] = useState(false);
  
  const scrollRef = useRef(null);
  const SCROLL_THRESHOLD = 0.95;

  useEffect(() => {
    checkAgreementStatus();
  }, []);

  const checkAgreementStatus = async () => {
    try {
      const response = await api.get('/users/host-agreement');
      setAgreementStatus(response.data);
      // Only show toast if NOT in view mode and agreement is accepted
      if (response.data.accepted && !isViewMode) {
        toast.info('You have already accepted the Host Service Agreement');
      }
    } catch (error) {
      console.error('Error checking agreement status:', error);
      toast.error('Failed to load agreement status');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const el = e.target;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;
    const scrollTop = el.scrollTop;
    
    if (scrollHeight <= clientHeight) {
      setScrollProgress(100);
      setScrolledToBottom(true);
      return;
    }
    
    const percent = (scrollTop + clientHeight) / scrollHeight;
    setScrollProgress(Math.min(100, Math.round(percent * 100)));
    
    if (percent >= SCROLL_THRESHOLD) {
      setScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!scrolledToBottom) {
      toast.error('Please scroll through the entire agreement first');
      return;
    }
    
    if (!termsRead) {
      toast.error('Please confirm you have read and understood the agreement');
      return;
    }
    
    if (typedSignature.trim().length < 2) {
      toast.error('Please type your full name as your electronic signature');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/users/accept-host-agreement', {
        typed_signature: typedSignature.trim(),
        scrolled_to_bottom: true
      });
      
      toast.success('Host Service Agreement accepted!');
      await refreshUser?.();
      
      // Automatically proceed to become host
      try {
        const becomeHostResponse = await api.post('/users/become-host');
        toast.success(becomeHostResponse.data.message || 'You are now a Host!');
        await refreshUser?.();
        navigate('/host');
      } catch (becomeHostError) {
        // If become-host fails, still redirect to owner dashboard
        navigate('/host');
      }
      
    } catch (error) {
      console.error('Error accepting agreement:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept agreement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // VIEW MODE: Show agreement content directly (read-only) for users who have already signed
  if (isViewMode && agreementStatus?.accepted) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <button 
              onClick={() => navigate('/host/agreements')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <Car className="w-6 h-6 text-[#004B8D]" />
              <span className="font-heading font-semibold">HireHub</span>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          {/* Title with signed badge */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Host Service Agreement</h1>
            <Badge variant="success" className="mb-2">
              <Check className="w-3 h-3 mr-1" /> Signed
            </Badge>
            <p className="text-slate-600 text-sm">
              Signed on {new Date(agreementStatus.agreement?.accepted_at).toLocaleDateString('en-AU', { dateStyle: 'long' })}
              {' '}• Version {agreementStatus.agreement?.agreement_version}
            </p>
          </div>

          {/* Agreement Content - Read Only */}
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ScrollText className="w-5 h-5 text-green-600" />
                  <CardTitle className="text-lg">Agreement Terms</CardTitle>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      toast.info('Generating download link...');
                      const res = await api.get('/users/host-agreement/download');
                      if (res.data?.download_url) {
                        window.open(res.data.download_url, '_blank');
                        toast.success('Agreement download started');
                      } else {
                        toast.error('Download URL not available');
                      }
                    } catch (err) {
                      console.error('PDF download error:', err);
                      toast.error('Failed to download PDF');
                    }
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div 
                className="max-h-[500px] overflow-y-auto border border-slate-200 rounded-lg p-6 bg-white prose prose-sm max-w-none"
                data-testid="agreement-view-container"
              >
                {/* Agreement Content */}
                <h2 className="text-lg font-bold text-center mb-4">{PLATFORM_NAME}</h2>
                <h3 className="text-md font-bold text-center mb-6">HOST SERVICE AGREEMENT</h3>
                
                <div className="bg-slate-50 p-4 rounded mb-6 text-sm">
                  <p><strong>Platform:</strong> {PLATFORM_NAME}</p>
                  <p><strong>ABN:</strong> {HIREHUB_ABN}</p>
                  <p><strong>Agreement Version:</strong> {agreementStatus.agreement?.agreement_version || '1.0'}</p>
                </div>

                <h4 className="font-bold mt-6 mb-2 text-blue-800">1. PARTIES</h4>
                <p>
                  This Host Service Agreement ("Agreement") is entered into between {PLATFORM_NAME} 
                  (ABN: {HIREHUB_ABN}), operating the HireHub vehicle rental platform ("Platform"), 
                  and you, the user accepting this agreement ("Host").
                </p>

                <h4 className="font-bold mt-6 mb-2 text-blue-800">2. HOST OBLIGATIONS</h4>
                <p>As a Host on the Platform, you agree to:</p>
                <ol className="list-decimal ml-6 space-y-1">
                  <li>Provide accurate and truthful information about listed vehicles.</li>
                  <li>Ensure all listed vehicles are roadworthy, registered, and adequately insured.</li>
                  <li>Maintain vehicles in clean and safe condition for renters.</li>
                  <li>Respond to booking requests within 24 hours.</li>
                  <li>Be available for vehicle handover at agreed times and locations.</li>
                  <li>Report any incidents, damage, or issues promptly through the Platform.</li>
                  <li>Comply with all applicable laws and regulations.</li>
                </ol>

                <h4 className="font-bold mt-6 mb-2 text-blue-800">3. PLATFORM FEES</h4>
                {isHostDirect ? (
                  <p>
                    HireHub operates in direct-payment mode for this Host. HireHub does not process
                    payments or deduct platform fees from bookings. Payment is arranged directly between
                    Host and Renter.
                  </p>
                ) : (
                  <p>
                    The Host agrees to pay Platform service fees as disclosed on the Platform. 
                    Fees are deducted from booking payments before Host payout. 
                    Current fee structure is available in the Host Dashboard.
                  </p>
                )}

                <h4 className="font-bold mt-6 mb-2 text-blue-800">4. PAYMENT TERMS</h4>
                {isHostDirect ? (
                  <p>
                    HireHub does not process payments in this mode. The Host receives payment directly
                    from the Renter via the Host's chosen payout method. The Platform acts solely as a
                    workflow, audit, and protection system — not a payment processor.
                  </p>
                ) : (
                  <p>
                    Payments are processed through our payment provider. Host payouts are made after booking completion 
                    and any applicable review period. The Platform reserves the right to withhold payments 
                    in case of disputes or claims.
                  </p>
                )}

                <h4 className="font-bold mt-6 mb-2 text-blue-800">5. CANCELLATION & DISPUTES</h4>
                {isHostDirect ? (
                  <p>
                    Hosts must adhere to the Platform's cancellation policy. Disputes between Host and
                    Renter will be handled through the Platform's dispute resolution process. In
                    direct-payment mode, HireHub does not hold funds or process payouts, so the Platform
                    cannot debit, withhold, or remit funds to settle claims. HireHub will review booking
                    evidence and issue a non-binding determination; any monetary settlement is arranged
                    directly between Host and Renter.
                  </p>
                ) : (
                  <p>
                    Hosts must adhere to the Platform's cancellation policy. Disputes between Host and Renter 
                    will be handled through the Platform's dispute resolution process. The Platform may 
                    debit the Host's future payouts for valid claims.
                  </p>
                )}

                <h4 className="font-bold mt-6 mb-2 text-blue-800">6. TERMINATION</h4>
                <p>
                  Either party may terminate this Agreement with written notice. The Platform reserves the 
                  right to suspend or terminate Host accounts for violation of these terms. Outstanding 
                  obligations survive termination.
                </p>

                <h4 className="font-bold mt-6 mb-2 text-blue-800">7. LIABILITY & INDEMNIFICATION</h4>
                <p>
                  The Host agrees to indemnify and hold harmless {PLATFORM_NAME} from any claims, damages, 
                  or losses arising from the Host's use of the Platform or breach of this Agreement. 
                  The Platform's liability is limited to the extent permitted by law.
                </p>

                <h4 className="font-bold mt-6 mb-2 text-blue-800">8. MODIFICATIONS</h4>
                <p>
                  {PLATFORM_NAME} reserves the right to modify this Agreement. Hosts will be notified of 
                  material changes and continued use of the Platform constitutes acceptance of modifications.
                </p>

                <h4 className="font-bold mt-6 mb-2 text-blue-800">9. GOVERNING LAW</h4>
                <p>
                  This Agreement is governed by the laws of the State of Queensland, Australia. 
                  Any disputes shall be resolved in the courts of Queensland.
                </p>

                <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Electronic Signature:</strong> {agreementStatus.agreement?.typed_signature || 'N/A'}
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Signed: {new Date(agreementStatus.agreement?.accepted_at).toLocaleString('en-AU')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Back button */}
          <div className="text-center">
            <Button variant="outline" onClick={() => navigate('/host/agreements')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Agreements
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // NON-VIEW MODE: Show "Already Accepted" redirect page (original behavior)
  if (agreementStatus?.accepted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle>Agreement Already Accepted</CardTitle>
              <CardDescription>
                You accepted the Host Service Agreement on{' '}
                {new Date(agreementStatus.agreement?.accepted_at).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-slate-600">
                Version: {agreementStatus.agreement?.agreement_version}
              </p>
              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => navigate('/host')}>
                  <Car className="w-4 h-4 mr-2" />
                  Go to Host Dashboard
                </Button>
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      toast.info('Generating download link...');
                      const res = await api.get('/users/host-agreement/download');
                      if (res.data?.download_url) {
                        window.open(res.data.download_url, '_blank');
                        toast.success('Agreement download started');
                      } else {
                        toast.error('Download URL not available');
                      }
                    } catch (err) {
                      console.error('PDF download error:', err);
                      toast.error('Failed to download agreement');
                    }
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Car className="w-6 h-6 text-[#004B8D]" />
            <span className="font-heading font-semibold">HireHub</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Host Service Agreement
          </h1>
          <p className="text-slate-600">
            Please read and accept the following agreement to become a Host on {PLATFORM_NAME}
          </p>
        </div>

        {/* Agreement Content */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-lg">Agreement Terms</CardTitle>
              </div>
              <Badge variant={scrolledToBottom ? "success" : "secondary"}>
                {scrollProgress}% read
              </Badge>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2">
              <div 
                className={`h-1.5 rounded-full transition-all ${
                  scrollProgress >= 95 ? 'bg-green-500' : scrollProgress >= 50 ? 'bg-yellow-500' : 'bg-blue-500'
                }`}
                style={{ width: `${scrollProgress}%` }}
              />
            </div>
          </CardHeader>
          <CardContent>
            <div 
              ref={scrollRef}
              onScroll={handleScroll}
              className="h-[400px] overflow-y-auto border border-slate-200 rounded-lg p-6 bg-white prose prose-sm max-w-none"
              data-testid="agreement-scroll-container"
            >
              <h2 className="text-lg font-bold text-center mb-4">{PLATFORM_NAME}</h2>
              <h3 className="text-md font-bold text-center mb-6">HOST SERVICE AGREEMENT</h3>
              
              <div className="bg-slate-50 p-4 rounded mb-6 text-sm">
                <p><strong>Platform:</strong> {PLATFORM_NAME}</p>
                <p><strong>ABN:</strong> {HIREHUB_ABN}</p>
                <p><strong>Agreement Version:</strong> 1.0</p>
              </div>

              <h4 className="font-bold mt-6 mb-2 text-blue-800">1. PARTIES</h4>
              <p>
                This Host Service Agreement ("Agreement") is entered into between {PLATFORM_NAME} 
                (ABN: {HIREHUB_ABN}), operating the HireHub vehicle rental platform ("Platform"), 
                and you, the user accepting this agreement ("Host").
              </p>

              <h4 className="font-bold mt-6 mb-2 text-blue-800">2. HOST OBLIGATIONS</h4>
              <p>As a Host on the Platform, you agree to:</p>
              <ol className="list-decimal ml-6 space-y-1">
                <li>Provide accurate and truthful information about listed vehicles.</li>
                <li>Ensure all listed vehicles are roadworthy, registered, and adequately insured.</li>
                <li>Maintain vehicles in clean and safe condition for renters.</li>
                <li>Respond to booking requests within 24 hours.</li>
                <li>Be available for vehicle handover at agreed times and locations.</li>
                <li>Report any incidents, damage, or issues promptly through the Platform.</li>
                <li>Comply with all applicable laws and regulations.</li>
              </ol>

              <h4 className="font-bold mt-6 mb-2 text-blue-800">3. PLATFORM FEES</h4>
              {isHostDirect ? (
                <p>
                  HireHub operates in direct-payment mode for this Host. HireHub does not process
                  payments or deduct platform fees from bookings. Payment is arranged directly between
                  Host and Renter.
                </p>
              ) : (
                <p>
                  The Host agrees to pay Platform service fees as disclosed on the Platform. 
                  Fees are deducted from booking payments before Host payout. 
                  Current fee structure is available in the Host Dashboard.
                </p>
              )}

              <h4 className="font-bold mt-6 mb-2 text-blue-800">4. PAYMENT TERMS</h4>
              {isHostDirect ? (
                <p>
                  HireHub does not process payments in this mode. The Host receives payment directly
                  from the Renter via the Host's chosen payout method. The Platform acts solely as a
                  workflow, audit, and protection system — not a payment processor.
                </p>
              ) : (
                <p>
                  Payments are processed through our payment provider. Host payouts are made after booking completion 
                  and any applicable review period. The Platform reserves the right to withhold payments 
                  in case of disputes or claims.
                </p>
              )}

              <h4 className="font-bold mt-6 mb-2 text-blue-800">5. CANCELLATION & DISPUTES</h4>
              {isHostDirect ? (
                <p>
                  Hosts must adhere to the Platform's cancellation policy. Disputes between Host and
                  Renter will be handled through the Platform's dispute resolution process. In
                  direct-payment mode, HireHub does not hold funds or process payouts, so the Platform
                  cannot debit, withhold, or remit funds to settle claims. HireHub will review booking
                  evidence and issue a non-binding determination; any monetary settlement is arranged
                  directly between Host and Renter.
                </p>
              ) : (
                <p>
                  Hosts must adhere to the Platform's cancellation policy. Disputes between Host and Renter 
                  will be handled through the Platform's dispute resolution process. The Platform may 
                  debit the Host's future payouts for valid claims.
                </p>
              )}

              <h4 className="font-bold mt-6 mb-2 text-blue-800">6. TERMINATION</h4>
              <p>
                Either party may terminate this Agreement with written notice. The Platform reserves the 
                right to suspend or terminate Host accounts for violation of these terms. Outstanding 
                obligations survive termination.
              </p>

              <h4 className="font-bold mt-6 mb-2 text-blue-800">7. LIABILITY & INDEMNIFICATION</h4>
              <p>
                The Host agrees to indemnify and hold harmless the Platform from any claims arising from 
                the Host's use of the service, including but not limited to vehicle defects, accidents, 
                or misrepresentation. The Platform's liability is limited to the fees paid by the Host.
              </p>

              <h4 className="font-bold mt-6 mb-2 text-blue-800">8. GOVERNING LAW</h4>
              <p>
                This Agreement is governed by the laws of New South Wales, Australia. 
                Any disputes shall be resolved in the courts of New South Wales.
              </p>

              <div className="mt-8 p-4 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>By accepting this agreement</strong>, you acknowledge that you have read, 
                  understood, and agree to be bound by all terms and conditions set forth above.
                </p>
              </div>
            </div>

            {!scrolledToBottom && (
              <div className="flex items-center justify-center gap-2 mt-4 text-amber-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>Please scroll through the entire agreement</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Acceptance Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Pen className="w-5 h-5 text-blue-600" />
              Electronic Signature
            </CardTitle>
            <CardDescription>
              Type your full legal name below as your electronic signature
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Read Confirmation */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms-read"
                checked={termsRead}
                onCheckedChange={setTermsRead}
                disabled={!scrolledToBottom}
                data-testid="terms-read-checkbox"
              />
              <label 
                htmlFor="terms-read" 
                className={`text-sm ${!scrolledToBottom ? 'text-slate-400' : 'text-slate-700'}`}
              >
                I have read and understand the Host Service Agreement. I agree to be bound by 
                its terms and conditions.
              </label>
            </div>

            {/* Signature Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Your Full Legal Name (Electronic Signature)
              </label>
              <Input
                type="text"
                placeholder="Type your full name"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                disabled={!scrolledToBottom || !termsRead}
                className={`font-serif italic ${!scrolledToBottom || !termsRead ? 'bg-slate-100' : ''}`}
                data-testid="signature-input"
              />
              {typedSignature && (
                <p className="text-xs text-slate-500 mt-1">
                  Signature preview: <span className="font-serif italic">{typedSignature}</span>
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              className="w-full bg-[#004B8D] hover:bg-[#003a6d]"
              size="lg"
              onClick={handleAccept}
              disabled={!scrolledToBottom || !termsRead || typedSignature.trim().length < 2 || submitting}
              data-testid="accept-agreement-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Accept Agreement & Become Host
                </>
              )}
            </Button>

            <p className="text-xs text-center text-slate-500">
              By clicking "Accept Agreement & Become Host", you are electronically signing this agreement.
              A PDF copy will be stored for your records.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default HostAgreementPage;
